import { useCallback } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TOOLTIP_DELAY_MS = 500;
const getInteractiveProps = (disableArtifacts, interactive) => {
  if (disableArtifacts) return {
    'aria-disabled': true
  };
  if (interactive) return {
    role: 'button',
    tabIndex: 0
  };
  return {};
};

// Renders the standard chip layout (icon + title + label + version badge)
// for any artifact type. Plugin authors who only need a different icon and
// label - not different content or interaction - can render this directly
// as their CHIP contribution instead of reimplementing the layout.
const ManagedArtifactChip = ({
  artifact,
  onArtifactClick,
  disableArtifacts,
  icon,
  translatedLabel,
  disabledTooltip
}) => {
  const handleClick = useCallback(() => {
    onArtifactClick === null || onArtifactClick === void 0 || onArtifactClick(artifact);
  }, [onArtifactClick, artifact]);
  const handleKeyDown = useCallback(event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  }, [handleClick]);
  const interactive = !disableArtifacts && !!onArtifactClick;
  return /*#__PURE__*/_jsx(View, {
    marginBlockStart: "400",
    children: /*#__PURE__*/_jsx(Tooltip, {
      disabled: !disableArtifacts,
      delay: TOOLTIP_DELAY_MS,
      title: disabledTooltip !== null && disabledTooltip !== void 0 ? disabledTooltip : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "artifacts.chip.disabledTooltip"
      }),
      children: /*#__PURE__*/_jsx("div", Object.assign({
        "data-block-type": "artifact"
      }, getInteractiveProps(!!disableArtifacts, interactive), {
        onClick: interactive ? handleClick : undefined,
        onKeyDown: interactive ? handleKeyDown : undefined,
        children: /*#__PURE__*/_jsx(CardWrapper, {
          cursor: interactive ? 'pointer' : undefined,
          padding: "0",
          borderRadius: "300",
          borderWidth: "100",
          borderStyle: "solid",
          borderColor: "border-core-subtle",
          children: /*#__PURE__*/_jsx(CardSection, {
            padding: "0",
            children: /*#__PURE__*/_jsxs(Flex, {
              alignItems: "center",
              paddingInline: "400",
              paddingBlock: "300",
              gap: "200",
              children: [/*#__PURE__*/_jsx(Flex, {
                backgroundColor: "fill-surface-recessed",
                borderRadius: "full",
                padding: "200",
                flexShrink: 0,
                alignItems: "center",
                justifyContent: "center",
                children: icon
              }), /*#__PURE__*/_jsxs(Flex, {
                flex: "1",
                flexDirection: "column",
                gap: "0",
                minInlineSize: "0",
                children: [/*#__PURE__*/_jsx(Text, {
                  typography: "heading-50",
                  truncate: true,
                  children: artifact.title
                }), /*#__PURE__*/_jsx(Text, {
                  typography: "detail-100",
                  children: translatedLabel
                })]
              }), artifact.currentVersion > 0 && /*#__PURE__*/_jsx(Text, {
                typography: "detail-100",
                paddingInlineStart: "200",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "artifacts.chip.version",
                  options: {
                    version: artifact.currentVersion
                  }
                })
              })]
            })
          })
        })
      }))
    })
  });
};
export default ManagedArtifactChip;